import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  GraduationCap, LogIn, Lock, Mail, CalendarDays, Clock, MapPin, Video,
  AlertCircle, CheckCircle2, Building2, Users, TrendingUp, BarChart3,
  FileText, Receipt, PenLine, Bell, Download, BookOpen, LogOut, Inbox,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LS_TOKEN = 'ifaap_portal_token';

// Portal tokens are stored in sessionStorage (cleared when the tab closes) for
// XSS hardening. We retain a single read-side migration from the legacy
// localStorage value so an in-flight learner session is not interrupted.
const portalToken = {
  get() {
    try {
      const fromSession = sessionStorage.getItem(LS_TOKEN);
      if (fromSession) return fromSession;
      const legacy = localStorage.getItem(LS_TOKEN);
      if (legacy) {
        sessionStorage.setItem(LS_TOKEN, legacy);
        localStorage.removeItem(LS_TOKEN);
        return legacy;
      }
      return null;
    } catch { return null; }
  },
  set(t) {
    try {
      if (t) {
        sessionStorage.setItem(LS_TOKEN, t);
        localStorage.removeItem(LS_TOKEN);
      } else {
        sessionStorage.removeItem(LS_TOKEN);
        localStorage.removeItem(LS_TOKEN);
      }
    } catch (e) { /* storage may be unavailable (private mode / quota) — best-effort */ console.warn("[storage]", e); }
  },
};

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return iso; }
}
function fmtEur(n) {
  const v = Number(n || 0);
  return `${v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}
function fmtRel(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "à l'instant";
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  return `il y a ${d} j`;
}

// =========================================================================
// Login forms
// =========================================================================
function LearnerView({ portal, onAuth, err, setErr }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${BACKEND}/api/portal/access`, {
        slug: portal.slug, email: email.trim(), access_code: code.trim(),
      });
      if (data.portal_token) portalToken.set(data.portal_token);
      onAuth({ role: 'learner', learner: data.learner, sessions: data.sessions || [], portal_token: data.portal_token });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Identifiants incorrects.');
    }
    setSubmitting(false);
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="portal-learner-form">
      <div>
        <label className="block text-[12px] font-semibold text-[#4A4B63] mb-1">Email professionnel</label>
        <div className="relative">
          <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
          <input data-testid="portal-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-10 pl-9 pr-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none" placeholder="vous@entreprise.fr" />
        </div>
      </div>
      <div>
        <label className="block text-[12px] font-semibold text-[#4A4B63] mb-1">Code d'accès apprenant</label>
        <div className="relative">
          <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
          <input data-testid="portal-code" required value={code} onChange={(e) => setCode(e.target.value)} className="w-full h-10 pl-9 pr-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none font-mono tracking-wider" placeholder="HUB-XXXXXX" />
        </div>
        <p className="text-[11px] text-[#8E8FA5] mt-1">Reçu par email de votre organisme de formation.</p>
      </div>
      {err && <div className="text-[12.5px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">{err}</div>}
      <button data-testid="portal-submit" disabled={submitting} type="submit" className="w-full h-10 rounded-xl brand-gradient text-white text-[13.5px] font-semibold flex items-center justify-center gap-1.5 shadow-sm disabled:opacity-60">
        <LogIn className="w-4 h-4" /> {submitting ? 'Connexion…' : 'Se connecter'}
      </button>
    </form>
  );
}

function CompanyView({ portal, onAuth, err, setErr }) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${BACKEND}/api/portal/company-access`, {
        slug: portal.slug, access_code: code.trim(),
      });
      if (data.portal_token) portalToken.set(data.portal_token);
      onAuth({ role: 'company', data, portal_token: data.portal_token });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Code invalide.');
    }
    setSubmitting(false);
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="portal-company-form">
      <div>
        <label className="block text-[12px] font-semibold text-[#4A4B63] mb-1">Code entreprise</label>
        <div className="relative">
          <Building2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
          <input data-testid="portal-company-code" required value={code} onChange={(e) => setCode(e.target.value)} className="w-full h-10 pl-9 pr-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none font-mono tracking-wider" placeholder="COMP-XXXXXXXX" />
        </div>
        <p className="text-[11px] text-[#8E8FA5] mt-1">Code fourni par votre organisme de formation pour l'accès RH.</p>
      </div>
      {err && <div className="text-[12.5px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">{err}</div>}
      <button data-testid="portal-company-submit" disabled={submitting} type="submit" className="w-full h-10 rounded-xl brand-gradient text-white text-[13.5px] font-semibold flex items-center justify-center gap-1.5 shadow-sm disabled:opacity-60">
        <LogIn className="w-4 h-4" /> {submitting ? 'Connexion…' : 'Accéder au tableau de bord'}
      </button>
    </form>
  );
}

// =========================================================================
// Shared UI bits
// =========================================================================
function StatusBadge({ status }) {
  const map = {
    'Payée': 'bg-[#E6FBF7] text-[#0F766E]',
    'En attente': 'bg-[#FEF3C7] text-[#92400E]',
    'En retard': 'bg-[#FEF2F2] text-[#B91C1C]',
    'Accepté': 'bg-[#E6FBF7] text-[#0F766E]',
    'Envoyé': 'bg-[#EFF6FF] text-[#1D4ED8]',
    'Brouillon': 'bg-[#F3F4F8] text-[#6B6C85]',
    'Converti': 'bg-[#EEF2FF] text-[#4F46E5]',
    'Refusé': 'bg-[#FEF2F2] text-[#B91C1C]',
  };
  const cls = map[status] || 'bg-[#F3F4F8] text-[#6B6C85]';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-semibold ${cls}`}>{status}</span>;
}

function DocLink({ docType, docId, token, label }) {
  const href = `${BACKEND}/api/portal/me/document/${docType}/${docId}.pdf?portal_token=${encodeURIComponent(token)}`;
  return (
    <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#4F46E5] hover:underline" data-testid={`dl-${docType}-${docId}`}>
      <Download className="w-3 h-3" /> {label || 'PDF'}
    </a>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <Icon className="w-8 h-8 text-[#B8B9CC] mb-2" />
      <p className="text-[12.5px] text-[#8E8FA5]">{text}</p>
    </div>
  );
}

// =========================================================================
// Documents tab (shared layout for learner + company)
// =========================================================================
function DocumentsTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND}/api/portal/me/documents?portal_token=${encodeURIComponent(token)}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="text-[13px] text-[#6B6C85] py-6">Chargement…</div>;
  if (!data || data.error) return <EmptyState icon={AlertCircle} text="Impossible de charger vos documents." />;

  const invoices = data.invoices || [];
  const quotes = data.quotes || [];
  const signatures = data.signatures || [];
  const sessions = data.sessions || [];
  const programmes = data.programmes || [];

  return (
    <div className="space-y-5">
      {/* Factures */}
      <section className="soft-card p-5" data-testid="docs-invoices">
        <header className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-[#E6FBF7] flex items-center justify-center"><Receipt className="w-4 h-4 text-[#14B8A6]" /></div>
          <h3 className="text-[14px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Factures ({invoices.length})</h3>
        </header>
        {invoices.length === 0 ? (
          <EmptyState icon={Inbox} text="Aucune facture disponible." />
        ) : (
          <div className="divide-y divide-[#F3F4F8]">
            {invoices.map((inv) => (
              <div key={inv.id} className="py-2.5 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">{inv.id} · {inv.client}</div>
                  <div className="text-[11px] text-[#6B6C85] truncate">{inv.description || 'Prestation de formation'} · {fmtDate(inv.date)}</div>
                </div>
                <div className="text-[12.5px] font-semibold text-[#1A1B2E]">{fmtEur(inv.amount)}</div>
                <StatusBadge status={inv.status} />
                <DocLink docType="invoice" docId={inv.id} token={token} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Devis */}
      <section className="soft-card p-5" data-testid="docs-quotes">
        <header className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-[#EEF2FF] flex items-center justify-center"><FileText className="w-4 h-4 text-[#4F46E5]" /></div>
          <h3 className="text-[14px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Devis ({quotes.length})</h3>
        </header>
        {quotes.length === 0 ? (
          <EmptyState icon={Inbox} text="Aucun devis disponible." />
        ) : (
          <div className="divide-y divide-[#F3F4F8]">
            {quotes.map((q) => (
              <div key={q.id} className="py-2.5 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">{q.id} · {q.program || q.client}</div>
                  <div className="text-[11px] text-[#6B6C85] truncate">Valide jusqu'au {fmtDate(q.validUntil)}</div>
                </div>
                <div className="text-[12.5px] font-semibold text-[#1A1B2E]">{fmtEur(q.amount)}</div>
                <StatusBadge status={q.status} />
                <DocLink docType="quote" docId={q.id} token={token} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Sessions / Convocations */}
      <section className="soft-card p-5" data-testid="docs-sessions">
        <header className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-[#FEF3C7] flex items-center justify-center"><CalendarDays className="w-4 h-4 text-[#F59E0B]" /></div>
          <h3 className="text-[14px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Convocations & sessions ({sessions.length})</h3>
        </header>
        {sessions.length === 0 ? (
          <EmptyState icon={Inbox} text="Aucune session pour le moment." />
        ) : (
          <div className="divide-y divide-[#F3F4F8]">
            {sessions.map((s) => (
              <div key={s.id} className="py-2.5 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">{s.title}</div>
                  <div className="text-[11px] text-[#6B6C85] truncate flex items-center gap-2 flex-wrap">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(s.startDate)}</span>
                    <span className="flex items-center gap-1">{s.mode === 'Distanciel' ? <Video className="w-3 h-3" /> : <MapPin className="w-3 h-3" />} {s.location}</span>
                    <span>· {s.duration}h</span>
                    <span>· {s.trainer}</span>
                  </div>
                </div>
                <DocLink docType="convocation" docId={s.id} token={token} label="Convocation" />
                <DocLink docType="emargement" docId={s.id} token={token} label="Émargement" />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Signatures */}
      <section className="soft-card p-5" data-testid="docs-signatures">
        <header className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-[#EFF6FF] flex items-center justify-center"><PenLine className="w-4 h-4 text-[#1D4ED8]" /></div>
          <h3 className="text-[14px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Émargements signés ({signatures.length})</h3>
        </header>
        {signatures.length === 0 ? (
          <EmptyState icon={Inbox} text="Aucune signature enregistrée pour l'instant." />
        ) : (
          <div className="divide-y divide-[#F3F4F8]">
            {signatures.slice(0, 30).map((sig) => (
              <div key={sig.id} className="py-2 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">{sig.signatory_name} · {sig.doc_type}</div>
                  <div className="text-[11px] text-[#6B6C85]">Signé le {fmtDate(sig.signed_at)} · Ref {sig.doc_id}</div>
                </div>
                {sig.doc_type === 'emargement' && <DocLink docType="emargement" docId={sig.doc_id} token={token} />}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Programmes */}
      {programmes.length > 0 && (
        <section className="soft-card p-5" data-testid="docs-programmes">
          <header className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#EEF2FF] flex items-center justify-center"><BookOpen className="w-4 h-4 text-[#4F46E5]" /></div>
            <h3 className="text-[14px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Fiches programmes ({programmes.length})</h3>
          </header>
          <div className="divide-y divide-[#F3F4F8]">
            {programmes.map((p) => (
              <div key={p.id} className="py-2 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">{p.title}</div>
                  <div className="text-[11px] text-[#6B6C85]">{p.category} · {p.duration}h · {p.mode}{p.nsf_code ? ` · NSF ${p.nsf_code}` : ''}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// =========================================================================
// Notifications tab
// =========================================================================
function NotificationsTab({ token }) {
  const [state, setState] = useState({ items: [], unread: 0, loading: true });
  const refresh = async () => {
    try {
      const { data } = await axios.get(`${BACKEND}/api/portal/me/notifications?portal_token=${encodeURIComponent(token)}`);
      setState({ items: data.items || [], unread: data.unread || 0, loading: false });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  };
  useEffect(() => { refresh(); }, [token]);

  const markAll = async () => {
    await axios.post(`${BACKEND}/api/portal/me/notifications/read-all?portal_token=${encodeURIComponent(token)}`);
    refresh();
  };

  if (state.loading) return <div className="text-[13px] text-[#6B6C85] py-6">Chargement…</div>;

  return (
    <div className="soft-card p-5" data-testid="notifications-tab">
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#EEF2FF] flex items-center justify-center"><Bell className="w-4 h-4 text-[#4F46E5]" /></div>
          <h3 className="text-[14px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Notifications {state.unread > 0 && <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#EF4444] text-white text-[10px] font-bold">{state.unread}</span>}
          </h3>
        </div>
        {state.unread > 0 && (
          <button onClick={markAll} className="text-[11.5px] font-semibold text-[#4F46E5] hover:underline" data-testid="mark-all-read">Tout marquer comme lu</button>
        )}
      </header>
      {state.items.length === 0 ? (
        <EmptyState icon={Bell} text="Aucune notification pour l'instant." />
      ) : (
        <div className="divide-y divide-[#F3F4F8]">
          {state.items.map((n) => (
            <div key={n.id} className={`py-3 flex items-start gap-3 ${n.read ? '' : 'bg-[#FAFAFE] -mx-2 px-2 rounded-lg'}`}>
              <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${n.read ? 'bg-[#E0E0EA]' : 'bg-[#4F46E5]'}`} />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[#1A1B2E]">{n.title}</div>
                <div className="text-[12px] text-[#6B6C85] mt-0.5">{n.description}</div>
                <div className="text-[10.5px] text-[#8E8FA5] mt-1">{fmtRel(n.created_at)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =========================================================================
// Company dashboard (tabs)
// =========================================================================
function CompanyDashboard({ data, token, onLogout }) {
  const kpis = data.kpis || {};
  return (
    <div data-testid="portal-company-dashboard">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-[24px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Tableau de bord · {data.company}
          </h1>
          <p className="text-[13px] text-[#6B6C85]">Vue consolidée des formations de vos collaborateurs.</p>
        </div>
        <button onClick={onLogout} className="text-[12px] text-[#6B6C85] hover:text-[#B91C1C] flex items-center gap-1" data-testid="portal-logout">
          <LogOut className="w-3.5 h-3.5" /> Déconnexion
        </button>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="inline-flex h-10 bg-[#F6F7FB] rounded-xl p-1 mb-5" data-testid="company-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-[12.5px] font-semibold rounded-lg px-4">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents" className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-[12.5px] font-semibold rounded-lg px-4">Documents</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications" className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-[12.5px] font-semibold rounded-lg px-4">Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Collaborateurs', value: kpis.learners, icon: Users, color: '#4F46E5' },
              { label: 'Sessions planifiées', value: kpis.sessions, icon: CalendarDays, color: '#14B8A6' },
              { label: 'Progression moy.', value: `${kpis.avg_progress || 0}%`, icon: TrendingUp, color: '#F59E0B' },
              { label: 'Formations finies', value: kpis.completed, icon: BarChart3, color: '#0EA5E9' },
            ].map((k) => (
              <div key={k.label} className="soft-card p-4">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: `${k.color}20`, color: k.color }}>
                  <k.icon className="w-4 h-4" />
                </div>
                <div className="text-[22px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{k.value}</div>
                <div className="text-[11.5px] text-[#8E8FA5] mt-0.5">{k.label}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="soft-card p-5">
              <h3 className="text-[14px] font-semibold text-[#1A1B2E] mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Collaborateurs</h3>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {(data.learners || []).map((l) => (
                  <div key={l.id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-[#FAFAFE]">
                    <div className="w-8 h-8 rounded-full brand-gradient text-white font-semibold flex items-center justify-center text-[11px] shrink-0">
                      {l.firstName?.[0]}{l.lastName?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">{l.firstName} {l.lastName}</div>
                      <div className="text-[11px] text-[#8E8FA5] truncate">{l.sessionsCount || 0} session(s) · {l.status}</div>
                    </div>
                    <div className="text-right">
                      <div className="w-[80px] h-1.5 bg-[#F3F4F8] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${l.progress || 0}%`, background: (l.progress || 0) >= 100 ? '#14B8A6' : '#4F46E5' }} />
                      </div>
                      <div className="text-[10.5px] text-[#6B6C85] mt-0.5">{l.progress || 0}%</div>
                    </div>
                  </div>
                ))}
                {(data.learners || []).length === 0 && <div className="text-[12px] text-[#8E8FA5] italic text-center py-4">Aucun collaborateur rattaché.</div>}
              </div>
            </div>

            <div className="soft-card p-5">
              <h3 className="text-[14px] font-semibold text-[#1A1B2E] mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Sessions récentes</h3>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {(data.sessions || []).slice(0, 10).map((s) => (
                  <div key={s.id} className="p-2.5 rounded-lg border border-[#F3F4F8]">
                    <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">{s.title}</div>
                    <div className="flex items-center gap-2 text-[11px] text-[#6B6C85] mt-1 flex-wrap">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(s.startDate)}</span>
                      <span className="flex items-center gap-1">{s.mode === 'Distanciel' ? <Video className="w-3 h-3" /> : <MapPin className="w-3 h-3" />} {s.location}</span>
                    </div>
                  </div>
                ))}
                {(data.sessions || []).length === 0 && <div className="text-[12px] text-[#8E8FA5] italic text-center py-4">Aucune session planifiée.</div>}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="documents"><DocumentsTab token={token} /></TabsContent>
        <TabsContent value="notifications"><NotificationsTab token={token} /></TabsContent>
      </Tabs>
    </div>
  );
}

// =========================================================================
// Learner dashboard (tabs)
// =========================================================================
function LearnerDashboard({ learner, sessions, token, onLogout }) {
  return (
    <div data-testid="portal-learner-dashboard">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-[24px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Bonjour {learner.firstName}, vos formations
          </h1>
          <p className="text-[13px] text-[#6B6C85]">Progression moyenne : <strong>{learner.progress}%</strong> · {learner.sessionsCount} session(s) suivie(s)</p>
        </div>
        <button onClick={onLogout} className="text-[12px] text-[#6B6C85] hover:text-[#B91C1C] flex items-center gap-1" data-testid="portal-logout">
          <LogOut className="w-3.5 h-3.5" /> Déconnexion
        </button>
      </div>

      <Tabs defaultValue="sessions">
        <TabsList className="inline-flex h-10 bg-[#F6F7FB] rounded-xl p-1 mb-5" data-testid="learner-tabs">
          <TabsTrigger value="sessions" data-testid="tab-learner-sessions" className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-[12.5px] font-semibold rounded-lg px-4">Mes formations</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-learner-documents" className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-[12.5px] font-semibold rounded-lg px-4">Documents</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-learner-notifications" className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-[12.5px] font-semibold rounded-lg px-4">Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="sessions">
          <div className="space-y-3">
            {sessions.length === 0 && (
              <div className="soft-card p-8 text-center text-[13px] text-[#6B6C85]">
                Vous n'êtes inscrit à aucune session pour le moment.
              </div>
            )}
            {sessions.map((s) => (
              <div key={s.id} data-testid={`portal-session-${s.id}`} className="soft-card p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl brand-gradient-soft flex flex-col items-center justify-center shrink-0">
                  <CalendarDays className="w-5 h-5 text-[#4F46E5]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold text-[#1A1B2E]">{s.title}</div>
                  <div className="flex items-center gap-3 text-[12px] text-[#6B6C85] mt-0.5 flex-wrap">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(s.startDate)}</span>
                    <span className="flex items-center gap-1">{s.mode === 'Distanciel' ? <Video className="w-3 h-3" /> : <MapPin className="w-3 h-3" />} {s.location}</span>
                    <span>· {s.duration}h · {s.trainer}</span>
                  </div>
                </div>
                <DocLink docType="convocation" docId={s.id} token={token} label="Convocation" />
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="documents"><DocumentsTab token={token} /></TabsContent>
        <TabsContent value="notifications"><NotificationsTab token={token} /></TabsContent>
      </Tabs>
    </div>
  );
}

// =========================================================================
// Main
// =========================================================================
export default function PortalPublic() {
  const { slug } = useParams();
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [auth, setAuth] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/portal/${slug}`)
      .then(({ data }) => setPortal({ ...data, slug }))
      .catch(() => setErr("Ce portail n'existe pas ou n'est plus actif."))
      .finally(() => setLoading(false));
  }, [slug]);

  const logout = async () => {
    const token = portalToken.get();
    if (token) {
      try { await axios.post(`${BACKEND}/api/portal/logout?portal_token=${encodeURIComponent(token)}`); } catch (e) { console.warn("[silent]", e); }
    }
    portalToken.set(null);
    setAuth(null);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAFAFE] text-[#6B6C85]">Chargement…</div>;
  if (err && !portal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFE]">
        <div className="soft-card p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-[#EF4444] mx-auto mb-3" />
          <h1 className="text-[18px] font-bold text-[#1A1B2E] mb-1">Portail introuvable</h1>
          <p className="text-[13px] text-[#6B6C85]">{err}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFE]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <header className="h-16 bg-white border-b border-[#EFF0F6] px-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl brand-gradient flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-[14px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Extranet {portal?.company}</div>
            <div className="text-[11px] text-[#8E8FA5]">Portail sécurisé · OFManager</div>
          </div>
        </div>
        {auth && (
          <div className="flex items-center gap-2 text-[12.5px] text-[#4A4B63]">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#E6FBF7] text-[#0F766E] font-semibold">
              <CheckCircle2 className="w-3 h-3" /> Connecté
            </span>
            <span className="font-semibold text-[#1A1B2E]">{auth.role === 'learner' ? `${auth.learner.firstName} ${auth.learner.lastName}` : `Accès entreprise · ${portal?.company}`}</span>
          </div>
        )}
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {!auth ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div>
              <h1 className="text-[28px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Espace formation dédié
              </h1>
              <p className="text-[14px] text-[#4A4B63] mt-3 leading-relaxed">
                Retrouvez ici les formations, documents pédagogiques, factures, devis et attestations délivrés à <strong>{portal?.company}</strong>.
              </p>
              <div className="mt-8 space-y-3">
                {[
                  { icon: Receipt, label: 'Factures & devis en ligne' },
                  { icon: PenLine, label: 'Feuilles d\'émargement signées' },
                  { icon: Bell, label: 'Notifications en temps réel' },
                  { icon: CalendarDays, label: 'Sessions & convocations' },
                ].map((s, i) => (
                  <div key={i} className="flex items-center gap-3 text-[13px] text-[#4A4B63]">
                    <div className="w-8 h-8 rounded-lg bg-[#EEF2FF] flex items-center justify-center"><s.icon className="w-4 h-4 text-[#4F46E5]" /></div>
                    {s.label}
                  </div>
                ))}
              </div>
            </div>

            <div className="soft-card p-6">
              <h2 className="text-[17px] font-bold text-[#1A1B2E] mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Connexion</h2>
              <p className="text-[12.5px] text-[#8E8FA5] mb-4">Accédez à votre espace personnel ou à la vue entreprise.</p>
              <Tabs defaultValue="learner">
                <TabsList data-testid="portal-tabs" className="grid grid-cols-2 w-full h-10 bg-[#F6F7FB] rounded-xl p-1 mb-4">
                  <TabsTrigger data-testid="tab-learner" value="learner" className="data-[state=active]:bg-white data-[state=active]:text-[#1A1B2E] data-[state=active]:shadow-sm text-[12.5px] font-semibold rounded-lg">Apprenant</TabsTrigger>
                  <TabsTrigger data-testid="tab-company" value="company" className="data-[state=active]:bg-white data-[state=active]:text-[#1A1B2E] data-[state=active]:shadow-sm text-[12.5px] font-semibold rounded-lg">Entreprise (RH)</TabsTrigger>
                </TabsList>
                <TabsContent value="learner"><LearnerView portal={portal} onAuth={setAuth} err={err} setErr={setErr} /></TabsContent>
                <TabsContent value="company"><CompanyView portal={portal} onAuth={setAuth} err={err} setErr={setErr} /></TabsContent>
              </Tabs>
              <Link to="/login" className="block text-center mt-4 text-[11.5px] text-[#8E8FA5] hover:text-[#4F46E5]">
                Formateur ou admin ? → Connexion OFManager
              </Link>
            </div>
          </div>
        ) : auth.role === 'company' ? (
          <CompanyDashboard data={auth.data} token={auth.portal_token} onLogout={logout} />
        ) : (
          <LearnerDashboard learner={auth.learner} sessions={auth.sessions} token={auth.portal_token} onLogout={logout} />
        )}

        <footer className="mt-12 text-center text-[11.5px] text-[#8E8FA5]">
          Propulsé par <span className="font-semibold">OFManager</span> · Portail sécurisé {portal?.portal}
        </footer>
      </main>
    </div>
  );
}
